// frontend/src/components/Settings/MpesaSettings.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

export default function MpesaSettings({ settings, onUpdate, loading }) {
  const [formData, setFormData] = useState({
    mpesa_enabled: false,
    mpesa_shortcode: '',
    mpesa_environment: 'sandbox',
    mpesa_paybill_name: '',
    business_phone: '',
    mpesa_consumer_key: '',
    mpesa_consumer_secret: '',
    mpesa_passkey: ''
  });

  // Load settings when component mounts or settings change
  useEffect(() => {
    if (settings) {
      setFormData({
        mpesa_enabled: settings.mpesa_enabled || false,
        mpesa_shortcode: settings.mpesa_shortcode || '',
        mpesa_environment: settings.mpesa_environment || 'sandbox',
        mpesa_paybill_name: settings.mpesa_paybill_name || '',
        business_phone: settings.business_phone || '',
        mpesa_consumer_key: settings.mpesa_consumer_key || '',
        mpesa_consumer_secret: settings.mpesa_consumer_secret || '',
        mpesa_passkey: settings.mpesa_passkey || ''
      });
    }
  }, [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.mpesa_enabled && !formData.mpesa_shortcode) {
      toast.error('Please enter your M-Pesa Paybill/Till number');
      return;
    }
    
    // Prepare data to save - this will go to both JSON and dedicated columns
    const submitData = {
      // Dedicated column fields
      mpesa_enabled: formData.mpesa_enabled,
      mpesa_shortcode: formData.mpesa_shortcode,
      mpesa_environment: formData.mpesa_environment,
      mpesa_paybill_name: formData.mpesa_paybill_name,
      business_phone: formData.business_phone,
      mpesa_consumer_key: formData.mpesa_consumer_key,
      mpesa_consumer_secret: formData.mpesa_consumer_secret,
      mpesa_passkey: formData.mpesa_passkey
    };
    
    onUpdate(submitData);
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">M-Pesa Payment Settings</h3>
      
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>⚠️ Important:</strong> Configure your M-Pesa details to receive payments directly to your 
          Safaricom Paybill/Till account. Customers will pay you directly, not through our system.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Enable M-Pesa */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <label className="font-medium text-gray-900">Enable M-Pesa Payments</label>
            <p className="text-sm text-gray-500">Allow customers to pay via M-Pesa</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.mpesa_enabled}
              onChange={(e) => setFormData({ ...formData, mpesa_enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        {formData.mpesa_enabled && (
          <>
            {/* Paybill/Till Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paybill/Till Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.mpesa_shortcode}
                onChange={(e) => setFormData({ ...formData, mpesa_shortcode: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 123456"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Your Safaricom Paybill or Till number</p>
            </div>
            
            {/* Business Name for Paybill */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paybill Business Name
              </label>
              <input
                type="text"
                value={formData.mpesa_paybill_name}
                onChange={(e) => setFormData({ ...formData, mpesa_paybill_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your business name as registered with Safaricom"
              />
            </div>
            
            {/* Environment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Environment
              </label>
              <select
                value={formData.mpesa_environment}
                onChange={(e) => setFormData({ ...formData, mpesa_environment: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="sandbox">Sandbox (Testing)</option>
                <option value="production">Production (Live)</option>
              </select>
            </div>
            
            {/* API Credentials */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-gray-900 mb-3">API Credentials</h4>
              <p className="text-sm text-gray-500 mb-3">
                Get these from the Safaricom Developer Portal after registering your Paybill
              </p>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Consumer Key
                  </label>
                  <input
                    type="text"
                    value={formData.mpesa_consumer_key}
                    onChange={(e) => setFormData({ ...formData, mpesa_consumer_key: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="Enter your consumer key"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Consumer Secret
                  </label>
                  <input
                    type="password"
                    value={formData.mpesa_consumer_secret}
                    onChange={(e) => setFormData({ ...formData, mpesa_consumer_secret: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="Enter your consumer secret"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Passkey
                  </label>
                  <input
                    type="password"
                    value={formData.mpesa_passkey}
                    onChange={(e) => setFormData({ ...formData, mpesa_passkey: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="Enter your passkey"
                  />
                </div>
              </div>
            </div>
            
            {/* Business Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Phone Number
              </label>
              <input
                type="tel"
                value={formData.business_phone}
                onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 0712345678"
              />
              <p className="text-xs text-gray-500 mt-1">For customer support and SMS notifications</p>
            </div>
          </>
        )}
        
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save M-Pesa Settings'}
          </button>
        </div>
      </form>
      
      <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
        <h4 className="font-medium text-yellow-800 mb-2">📞 How to Get M-Pesa Credentials</h4>
        <ol className="text-sm text-yellow-700 space-y-1 list-decimal list-inside">
          <li>Register for a Paybill/Till number via *334# or Safaricom Business</li>
          <li>Go to https://developer.safaricom.co.ke/</li>
          <li>Create an account and register your app</li>
          <li>Select "M-Pesa" API product</li>
          <li>Get your Consumer Key, Consumer Secret, and Passkey</li>
          <li>Contact Safaricom to activate live credentials</li>
        </ol>
      </div>
    </div>
  );
}